import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";

export const TrendChart = ({ className = "mx-auto" }) => {
  const chartRef = useRef(null);
  const barsRef = useRef<(SVGRectElement | null)[]>([]);
  const lineRef = useRef<SVGPolylineElement>(null);

  useGSAP(() => {
    const bars = barsRef.current.filter(Boolean);
    const line = lineRef.current;

    if (!bars.length || !line) return;

    // Create infinite loop timeline
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 1 });

    // Initially hide all bars by setting their height to 0 from bottom
    gsap.set(bars, {
      scaleY: 0.01,
      transformOrigin: "bottom center",
    });

    // Hide the trend line initially
    gsap.set(line, {
      strokeDasharray: "1000",
      strokeDashoffset: "1000",
    });

    // Animate bars growing from bottom with stagger
    tl.to(bars, {
      scaleY: 1,
      duration: 3,
      stagger: 0.2,
      ease: "back.out(1.7)",
    })

      // Animate trend line drawing
      .to(
        line,
        {
          strokeDashoffset: 0,
          duration: 3,
          ease: "power2.out",
        },
        "-=0.5"
      )

      // Reset for next loop
      .set(bars, { scaleY: 0.01 })
      .set(line, { strokeDashoffset: "1000" });
  });

  return (
    <svg
      ref={chartRef}
      id="trend-chart"
      viewBox="0 0 200 200"
      className={className}
      width={150}
      height={150}
    >
      <defs>
        <linearGradient
          id="linear-gradient"
          x1="31.71"
          y1="187.9"
          x2="33.57"
          y2="-114"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0d2949" />
          <stop offset="1" stopColor="#52b773" />
        </linearGradient>
        <linearGradient
          id="linear-gradient-2"
          x1="60.51"
          y1="193.26"
          x2="58.64"
          y2="-57.39"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#43b0da" />
          <stop offset="1" stopColor="#52b773" />
        </linearGradient>
        <linearGradient
          id="linear-gradient-3"
          x1="117.23"
          y1="185.86"
          x2="112.57"
          y2="-44.29"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#f89c6b" />
          <stop offset="1" stopColor="#cdcd81" />
        </linearGradient>
        <linearGradient
          id="linear-gradient-4"
          x1="142.86"
          y1="185.91"
          x2="144.73"
          y2="-6.04"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#666766" />
          <stop offset="1" stopColor="#939598" />
        </linearGradient>
        <linearGradient
          id="linear-gradient-5"
          x1="171.38"
          y1="-5.1"
          x2="171.38"
          y2="185.92"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#f7f7f7" />
          <stop offset="1" stopColor="#a7a9ac" />
        </linearGradient>
      </defs>

      <g>
        <rect
          ref={(el) => {
            barsRef.current[0] = el;
          }}
          style={{ fill: "url(#linear-gradient)" }}
          x="18.24"
          y="42.8"
          width="27.84"
          height="142.21"
        />

        <rect
          ref={(el) => {
            barsRef.current[1] = el;
          }}
          style={{ fill: "url(#linear-gradient-2)" }}
          x="46.09"
          y="67.39"
          width="27.84"
          height="117.62"
        />

        <rect
          ref={(el) => {
            barsRef.current[2] = el;
          }}
          style={{ fill: "#52b773" }}
          x="73.93"
          y="14.99"
          width="27.84"
          height="170.02"
        />

        <rect
          ref={(el) => {
            barsRef.current[3] = el;
          }}
          style={{ fill: "url(#linear-gradient-3)" }}
          x="101.77"
          y="35.02"
          width="27.84"
          height="149.99"
        />

        <rect
          ref={(el) => {
            barsRef.current[4] = el;
          }}
          style={{ fill: "url(#linear-gradient-2)" }}
          x="129.61"
          y="48.33"
          width="27.84"
          height="136.68"
        />

        <rect
          ref={(el) => {
            barsRef.current[5] = el;
          }}
          style={{ fill: "url(#linear-gradient-5)" }}
          x="157.46"
          y="22.39"
          width="27.84"
          height="162.62"
        />
      </g>

      <polyline
        ref={lineRef}
        style={{
          fill: "none",
          stroke: "#f7f7f7",
          strokeMiterlimit: 10,
          strokeWidth: "6.45px",
        }}
        points="2.71 90.26 60.01 126.2 89.62 91.31 143.53 73.6 197.29 101.51"
      />
    </svg>
  );
};
